import React, {useEffect, useState, useRef, useMemo} from 'react';
import {Button, Modal, Table} from "semantic-ui-react";
import styles from '../Checklist.module.scss';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus, faTrash, faCopy, faLockOpen, faLock} from "@fortawesome/pro-regular-svg-icons";
import FirebaseUsage from "../../../../../firebase/firebase.usage";
import {COLLECTIONS} from "../../../../../firebase/constants";
import {useUserSelector} from "../../../../../store/selectors/authorization.selectors";
import uuid from "uuid";
import {CheckListModel} from "../../../../../models/checklist-model";
import {useActiveProjectSelector, useChecklistMapSelector} from "../../../../../store/selectors/project.selectors";
import {subsManager} from "../../../../../store/middleware/subs-manager/subs-manager";

export default function NewChecklistModal(props: {
    isOpen: boolean,
    setOpen: any,
    clientId: string,
    taskId: string | null,
    activeChecklistId: string | null,
}) {
    const { isOpen, setOpen, clientId, taskId, activeChecklistId } = props;

    // const [checkLists, setCheckLists] = useState<CheckListModel[]>([]);
    const checkListMap = useChecklistMapSelector();
    const [checkListItems, setCheckListItems] = useState<any[]>([]);
    const [selectedChecklist, setSelectedChecklist] = useState<CheckListModel | null>(null);
    const [selectedChecklistItem, setSelectedChecklistItem] = useState<any>(null);
    const [addingChecklist, setAddingChecklist] = useState(false);
    const [addingChecklistItem, setAddingChecklistItem] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [editChecklistId, setEditChecklistId] = useState<string | null>(null);
    const [editChecklistItemTimeId, setEditChecklistItemTimeId] = useState<string | null>(null);
    const [editChecklistItemTimeValue, setEditChecklistItemTimeValue] = useState<number>(0);
    const [openLockModal, setOpenLockModal] = useState(false);
    const activeProject = useActiveProjectSelector();
    const user = useUserSelector();

    // async function subscribeToChecklists() {
    //     unsubscribeRef.current = await FirebaseUsage.queryListener(COLLECTIONS.CHECKLISTS, [['clientId', '==', clientId]], (data: any) =>
    //         setCheckLists(data.docs.map((doc: any) => doc.data() as CheckListModel).sort((a: any, b: any) =>
    //             a.checklistName.localeCompare(b.checklistName)
    //         )))
    // }

    // useEffect(() => {
    //     subscribeToChecklists().catch(e => console.error(e));
    // }, []);

    const checkLists = useMemo(() => {
        if (!checkListMap) return [];
        const checklistIds = checkListMap.get('checklists')
        if (!checklistIds) return [];
        return checklistIds.map((checklistId: string) => checkListMap.get(checklistId))
    }, [checkListMap, clientId]);

    useEffect(() => {
        if (activeChecklistId) {
            const newSelectedChecklist = checkLists.find(checklist => checklist.checklistId === activeChecklistId);
            if (!newSelectedChecklist) return
            setSelectedChecklist(newSelectedChecklist);
        }
    }, [activeChecklistId]);

    useEffect(() => {
        if (selectedChecklist) {
            const newSelectedChecklist = checkLists.find(checklist => checklist.checklistId === selectedChecklist.checklistId);
            if (!newSelectedChecklist) return
            setCheckListItems(
                newSelectedChecklist.items
                    // .sort((a: any, b: any) => a.itemName.localeCompare(b.itemName))
                    // .sort((a: any, b: any) => a.tMinusDays - b.tMinusDays)
                || []);
            setSelectedChecklist(newSelectedChecklist);
        } else {
            setCheckListItems([]);
        }
    }, [selectedChecklist, checkLists]);

    const generateUniqueName = (name: string) => {
        let uniqueName = name;
        if (checkLists.some(checklist => checklist.checklistName === uniqueName)) {
            let suffix = 0;
            while (checkLists.some(checklist => checklist.checklistName === uniqueName)) {
                suffix++;
                uniqueName = `${name}_${suffix}`;
            }
        }
        return uniqueName;
    }

    const addNewChecklist = async (name: string) => {
        let uniqueName = name;
        if (!name) return;
        if (name === "") uniqueName = 'New ChecklistPage';
        uniqueName = generateUniqueName(uniqueName);
        const newChecklistDoc = FirebaseUsage.getBlankDoc(COLLECTIONS.CHECKLISTS)
        const dateAdded = FirebaseUsage.timestamp();
        const checklistData = {
            checklistId: newChecklistDoc.id,
            checklistName: uniqueName,
            dateAdded,
            uuid: `CL-${dateAdded.seconds}`,
            createdBy: user!.userId,
            clientId: clientId,
            items: [] as any
        }
        await FirebaseUsage.setDocumentWithDoc(newChecklistDoc, checklistData);
        setSelectedChecklist(checklistData);
        if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
    }

    const copyChecklist = async () => {
        if (!selectedChecklist) return;
        const newChecklistName = generateUniqueName(selectedChecklist.checklistName);
        const newChecklistDoc = FirebaseUsage.getBlankDoc(COLLECTIONS.CHECKLISTS)
        const dateAdded = FirebaseUsage.timestamp();
        const checklistData = {
            checklistId: newChecklistDoc.id,
            checklistName: newChecklistName,
            dateAdded,
            uuid: `CL-${dateAdded.seconds}`,
            createdBy: user!.userId,
            clientId,
            items: selectedChecklist.items
        }
        await FirebaseUsage.setDocumentWithDoc(newChecklistDoc, checklistData);
        setSelectedChecklist(checklistData);
        if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
    }

    const addNewChecklistItem = async (name: string) => {
        if (!name || !selectedChecklist) return;
        const newChecklistItem = {
            checklistItemId: uuid.v4(),
            itemName: name,
            status: 'not started',
            tMinusDays: -30,
        }
        let items = selectedChecklist!.items
        items.push(newChecklistItem)
        await FirebaseUsage.updateDoc(COLLECTIONS.CHECKLISTS, selectedChecklist!.checklistId, { items: items })
        setSelectedChecklistItem(newChecklistItem);
        if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
    }

    const renameChecklist = async (name: string, checklistId: string) => {
        if (!name || !checklistId) return;
        const newChecklistName = generateUniqueName(name);
        await FirebaseUsage.updateDoc(COLLECTIONS.CHECKLISTS, checklistId, { checklistName: newChecklistName })
        if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
    }

    const deleteChecklist = async () => {
        if (!selectedChecklist) return;
        await FirebaseUsage.deleteDocument(COLLECTIONS.CHECKLISTS, selectedChecklist.checklistId);
        setSelectedChecklist(null);
        if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
    }

    const deleteChecklistItem = async () => {
        if (!selectedChecklist || !selectedChecklistItem) return;
        const items = selectedChecklist.items.filter((item: any) => item.checklistItemId !== selectedChecklistItem.checklistItemId);
        await FirebaseUsage.updateDoc(COLLECTIONS.CHECKLISTS, selectedChecklist.checklistId, { items });
        setSelectedChecklistItem(null);
        if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
    }

    const lockChecklist = async () => {
        if (!selectedChecklist) return;
        await FirebaseUsage.updateDoc(COLLECTIONS.CHECKLISTS, selectedChecklist.checklistId, { locked: true });
        if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
    }

    const assignChecklistToTask = async () => {
        if (!selectedChecklist || !taskId) return;
        await FirebaseUsage.updateDoc(COLLECTIONS.TASKS, taskId, {
            checklistId: selectedChecklist.checklistId,
        });
        // await createChecklistTasks();
        setOpen(false);
    }

    const createChecklistTasks = async () => {
        let batch: any = []
        if (!selectedChecklist) return;
        checkListItems.forEach((item: any) => {
            const docRef = FirebaseUsage.getBlankDoc(COLLECTIONS.CHECKLIST_TASKS);
            batch.push({ref: docRef, type: 'set', data: {
                taskId,
                clientId,
                checklistId: selectedChecklist.checklistId,
                checklistItemId: item.checklistItemId,
                checklistTaskId: docRef.id,
                complete: false,
                itemName: item.itemName,
            }})
        })
    }

    const removeChecklistFromTask = async () => {
        if (!taskId) return;
        await FirebaseUsage.updateDoc(COLLECTIONS.TASKS, taskId, {
            checklistId: null,
        });
    }

    return (
        <Modal open={isOpen} onClose={() => {
            setOpen(false)
        }} dimmer={'blurring'} size={'large'} closeIcon closeOnEscape={false}
        >
            <Modal.Header><h3>Checklist Library</h3></Modal.Header>
            <Modal.Content>
                <div className={styles.ModalLabels}>
                    <p></p>
                    <label><strong>Checklists</strong></label>
                    <label><strong>Checklist Items {selectedChecklist ?
                        selectedChecklist.locked ?
                            '(locked)' : '(unlocked)' : ''
                    }</strong></label>
                    <p></p>
                </div>
                <div className={styles.ModalContainer}>
                    <div className={styles.ChecklistActions}>
                        <div className={styles.ActionButton} onClick={() => setAddingChecklist(true)}>
                            <FontAwesomeIcon icon={faPlus} size="lg" />
                        </div>
                        <div className={checkLists.length && selectedChecklist ? styles.ActionButton : styles.ActionButtonDisabled}
                                onClick={() => {
                                    if (selectedChecklist) {
                                        copyChecklist().catch(e => console.error(e));
                                    }
                                }}>
                            <FontAwesomeIcon icon={faCopy} size="lg" />
                        </div>
                        <div className={checkLists.length && selectedChecklist ? styles.ActionButton : styles.ActionButtonDisabled}
                             onClick={() => setOpenDeleteModal(true)}>
                            <FontAwesomeIcon icon={faTrash} size="lg" />
                        </div>
                    </div>
                    <div className={styles.ExistingChecklists}>
                        {addingChecklist &&
                            <input
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        if (name) {
                                            addNewChecklist(name).then(() => setAddingChecklist(false));
                                        }
                                    } else if (e.key === 'Escape') {
                                        setAddingChecklist(false);
                                    }
                                }}
                                onBlur={(e) => {
                                    if (e.target.value) {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        addNewChecklist(name).then(() => setAddingChecklist(false));
                                    } else {
                                        setAddingChecklist(false)
                                    }
                                }}
                                className={styles.NewChecklistInput}
                                type="text"
                                placeholder="New Checklist" />
                        }
                        {checkLists.map((checklist, index) => editChecklistId && editChecklistId === checklist.checklistId ? (
                            <input
                                autoFocus
                                key={index}
                                placeholder={checklist.checklistName}
                                className={styles.NewChecklistInput}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        if (name) {
                                            renameChecklist(name, checklist.checklistId).then(() => setEditChecklistId(null));
                                        }
                                    } else if (e.key === 'Escape') {
                                        setEditChecklistId(null);
                                    }
                                }}
                                onBlur={(e) => {
                                    if (e.target.value) {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        renameChecklist(name, checklist.checklistId).then(() => setEditChecklistId(null));
                                    } else {
                                        setEditChecklistId(null);
                                    }
                                }}
                            />
                        ) : (
                            <div key={index} className={styles.ChecklistEntry}
                                 style={selectedChecklist?.checklistId === checklist.checklistId ? {
                                     backgroundColor: 'rgba(203, 230, 255, 0.7)',
                                     fontWeight: 'bold',
                                 } : {}}
                                 onDoubleClick={() => setEditChecklistId(checklist.checklistId)}
                                 onClick={() => setSelectedChecklist(selectedChecklist && selectedChecklist.checklistId === checklist.checklistId ? null : checklist)}>
                                <FontAwesomeIcon style={{marginRight:  "5px"}} icon={checklist.locked ? faLock : faLockOpen}/>{checklist.checklistName} ({checklist.uuid})
                            </div>
                        ))}
                    </div>
                    <div className={styles.EditChecklist} style={selectedChecklist ? {} : {backgroundColor: 'rgba(241,239,239,0.4)'}}>
                        {addingChecklistItem  && selectedChecklist && !selectedChecklist.locked && <div className={styles.ChecklistItemInput}>
                            <input
                                className={styles.ChecklistNameInput}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        if (name) {
                                            addNewChecklistItem(name).then(() => setAddingChecklistItem(false));
                                        }
                                    } else if (e.key === 'Escape') {
                                        setAddingChecklistItem(false);
                                    }
                                }}
                                onBlur={(e) => {
                                    if (e.target.value) {
                                        // @ts-ignore
                                        const name = e.target.value;
                                        addNewChecklistItem(name).then(() => setAddingChecklistItem(false));
                                    } else {
                                        setAddingChecklistItem(false);
                                    }
                                }}
                                type='text'
                                placeholder='New Checklist Item'
                            />
                        </div>}
                        {selectedChecklist && selectedChecklist.items.length > 0 &&
                            checkListItems.map((item, index) => (
                            <div key={`item-${index}`} className={styles.ChecklistItemEntry}
                                 style={selectedChecklistItem && selectedChecklistItem.checklistItemId === item.checklistItemId ? {
                                     backgroundColor: 'rgba(203, 230, 255, 0.7)',
                                     fontWeight: 'bold',
                                 } : {}}
                                 onClick={() => setSelectedChecklistItem(item)}
                            >
                                <div className={styles.ChecklistItemName}>{item.itemName}</div>
                                {editChecklistItemTimeId === item.checklistItemId && selectedChecklistItem && selectedChecklistItem.checklistItemId === item.checklistItemId ?
                                    <input
                                        autoFocus
                                        className={styles.ChecklistItemTimeInput}
                                        type='number'
                                        value={editChecklistItemTimeValue}
                                        // @ts-ignore
                                        onChange={(e) => setEditChecklistItemTimeValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                if (editChecklistItemTimeValue) {
                                                    FirebaseUsage.updateDoc(COLLECTIONS.CHECKLISTS, selectedChecklist!.checklistId, {
                                                        items: checkListItems.map((item: any) => item.checklistItemId === editChecklistItemTimeId ? {
                                                            ...item,
                                                            tMinusDays: editChecklistItemTimeValue,
                                                        } : item)
                                                    }).then(() => {
                                                        setEditChecklistItemTimeId(null)
                                                        if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
                                                    });
                                                }
                                            } else if (e.key === 'Escape') {
                                                setEditChecklistItemTimeId(null);
                                            }
                                        }}
                                        onBlur={() => {
                                            if (editChecklistItemTimeValue && editChecklistItemTimeId && selectedChecklist && editChecklistItemTimeValue !== item.tMinusDays) {
                                                FirebaseUsage.updateDoc(COLLECTIONS.CHECKLISTS, selectedChecklist!.checklistId, {
                                                    items: checkListItems.map((item: any) => item.checklistItemId === editChecklistItemTimeId ? {
                                                        ...item,
                                                        tMinusDays: editChecklistItemTimeValue,
                                                    } : item)
                                                }).then(() => {
                                                    setEditChecklistItemTimeId(null)
                                                    if (activeProject && activeProject.clientId) subsManager.subscribeChecklists(activeProject.clientId)
                                                });
                                            } else {
                                                setEditChecklistItemTimeId(null);
                                            }
                                        }}
                                    /> :
                                    <div className={styles.ChecklistItemTime}
                                         onClick={() => {
                                             if (selectedChecklist && !selectedChecklist.locked) {
                                                 setEditChecklistItemTimeValue(item.tMinusDays)
                                                 setEditChecklistItemTimeId(item.checklistItemId)
                                             }
                                         }}
                                    >Task {item.tMinusDays <= 0 ? 'start' : 'finish'}  {item.tMinusDays <= 0 ? 'minus' : 'plus'} {Math.abs(item.tMinusDays)} days</div>
                                }
                            </div>
                        ))}
                    </div>
                    <div className={styles.ChecklistItemActions}>
                        <div className={selectedChecklist && selectedChecklist && !selectedChecklist.locked ? styles.ActionButton : styles.ActionButtonDisabled}
                             onClick={() => setAddingChecklistItem(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} size="lg" />
                        </div>
                        <div className={checkListItems.length && selectedChecklistItem && selectedChecklist && !selectedChecklist.locked ? styles.ActionButton : styles.ActionButtonDisabled}
                                onClick={() => {
                                    if (selectedChecklistItem && selectedChecklist && !selectedChecklist.locked) {
                                        deleteChecklistItem().catch(e => console.error(e));
                                    }
                                }}>
                            <FontAwesomeIcon icon={faTrash} size="lg" />
                        </div>
                        <div className={selectedChecklist && !selectedChecklist.locked && checkListItems.length ? styles.ActionButton : styles.ActionButtonDisabled}
                             onClick={() => {
                                 if (selectedChecklist && selectedChecklist && !selectedChecklist.locked && checkListItems.length) {
                                        setOpenLockModal(true);
                                 }}}>
                            <FontAwesomeIcon icon={selectedChecklist && !selectedChecklist.locked ? faLockOpen : faLock} size="lg" />
                        </div>
                    </div>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => {
                    setOpen(false);
                }}>Close</Button>
                {taskId && (!activeChecklistId || activeChecklistId !== selectedChecklist?.checklistId) &&
                    <Button positive disabled={!selectedChecklist || !checkListItems.length || !selectedChecklist.locked}
                    onClick={() => assignChecklistToTask().then(() => setOpen(false))}>
                    Select
                </Button>}
                {taskId && activeChecklistId === selectedChecklist?.checklistId &&
                    <Button negative disabled={!selectedChecklist || !checkListItems.length || !selectedChecklist.locked}
                            onClick={() => removeChecklistFromTask().then(() => setOpen(false))}>
                        Remove
                    </Button>}
            </Modal.Actions>
            <Modal size={'mini'} open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
                <Modal.Header>Delete Checklist</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete this checklist?</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
                    <Button negative onClick={() => {
                        deleteChecklist().catch(e => console.error(e));
                        setOpenDeleteModal(false);
                    }}>Delete</Button>
                </Modal.Actions>
            </Modal>
            <Modal size={'mini'} open={openLockModal} onClose={() => setOpenLockModal(false)}>
                <Modal.Header>Locking Checklist</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to lock this checklist? This action cannot be undone.</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button negative onClick={() => setOpenLockModal(false)}>Cancel</Button>
                    <Button positive onClick={() => {
                        lockChecklist().catch(e => console.error(e));
                        setOpenLockModal(false);
                    }}>Lock</Button>
                </Modal.Actions>
            </Modal>
        </Modal>
    )
}